import { ReactElement, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import 'moment-timezone';
import 'react-datepicker/dist/react-datepicker.css';
import html2canvas from 'html2canvas';
import Procard from './Procard';
import InfoCard from './InfoCard';
import DropdownControl from '../controls/DropdownControl';
import {
  DashboardDataFilter,
  Group,
  Option,
  TimeSpan,
  TimeSpanDateAttributes
} from '../constants/telosair-interfaces';
import {
  DAHBOARD_DEFAULT_FILTER,
  IconSourceDescription,
  OVERVIEW_GROUP
} from '../constants/global-constants';
import DashboardSyncCountDown from './DashboardSyncCountdown';
import RefreshButton from './RefreshButton';
import { downloadFile } from '../_helpers/dashboardHelper';
import { useData } from '../context/data.context';
import { Filters, setAppliedFilter } from '../services/localstorage.service';
import DateRangePicker from '../controls/DateRangePicker';
import ClientChange from './ClientChange';
import { GA_CATEGORIES, sendCustomEventToGa } from '../_helpers/ga';
import ViewModeToggle, { DashboardView } from '../controls/ViewModeToggle';
import { getResolutionOptions, resolutionLabel } from '../_helpers/timeResolution';
import usePlan from '../hooks/usePlan';

type DashboardFiltersProps = {
  onChangeTimeFilter: (span: string | TimeSpan) => void;
  lastSynced: Date | null;
  groupedData: Group[];
  dataFilter: DashboardDataFilter | null;
  setDataFilter: (filter: DashboardDataFilter) => void;
  onRefresh: () => void;
  refreshing: boolean;
  timeSpanDate: TimeSpanDateAttributes;
  setTimeSpanDate: (value: TimeSpanDateAttributes) => void;
  timeSpan: TimeSpan;
  handleDownload: () => void;
  handleIngest: () => void;
  view: DashboardView;
  setView: (value: DashboardView) => void;
  /**
   * Views the current user may switch to. Options outside this list are hidden
   * (feature-gated); an empty list hides the toggle entirely.
   */
  availableViews?: DashboardView[];
  /** Chart bucket width (the API's dataPointsDifference), e.g. "15m". */
  resolution: string;
  setResolution: (value: string) => void;
};

/**
 * DashboardFilters component for rendering DropDown for Filters and a variety of actions on the Dashboard.
 * @param props - The props for the DashboardFilters component.
 * @returns  A React element representing the DashboardFilters component.
 */
export default function DashboardFilters(props: DashboardFiltersProps): ReactElement {
  const {
    onChangeTimeFilter,
    lastSynced,
    groupedData,
    refreshing,
    timeSpanDate,
    timeSpan,
    dataFilter,
    onRefresh,
    setTimeSpanDate,
    setDataFilter,
    handleDownload,
    handleIngest,
    view,
    setView,
    availableViews = ['simple', 'detailed', 'map'],
    resolution,
    setResolution
  } = props;
  const { userConfigData, currentUser } = useData();
  const { planLevel, maxDataDays } = usePlan();
  const [customDatePicker, setCustomDatePicker] = useState(false);
  const GROUPS: Option<number>[] = groupedData.map((item) => ({
    name: item.groupName,
    id: item.groupId,
    childOptions: item.sensorDetailsList?.map((device) => ({
      name: device.deviceName,
      id: device.serial_number
    }))
  }));

  // Advanced plan (level 2) and above have real-time access
  const hasRealTimeAccess = planLevel >= 2;

  const TIME_SPANS: Option<string>[] = Object.values(TimeSpan)
    .filter((item) => {
      if (item === TimeSpan.RealTime && !hasRealTimeAccess) return false;
      if (item === TimeSpan.Week1 && maxDataDays !== null && maxDataDays < 7) return false;
      if (item === TimeSpan.Month1 && maxDataDays !== null && maxDataDays < 30) return false;
      return true;
    })
    .map((item) => ({ name: item, id: item }));

  // Restrict how far back users can go in the custom date picker
  const customMinDate =
    maxDataDays === null
      ? null
      : new Date(Date.now() - Math.max(maxDataDays, 1) * 24 * 60 * 60 * 1000);

  // If the current timeSpan is no longer valid for this plan, reset to default
  useEffect(() => {
    if (timeSpan === TimeSpan.RealTime && !hasRealTimeAccess) {
      onChangeTimeFilter(TimeSpan.Mins15);
    }
  }, [hasRealTimeAccess, timeSpan]);

  /**
   * Handles screen capture and image download.
   */
  const handleScreenCapture = async (): Promise<void> => {
    const canvas = await html2canvas(document.body);
    const dataURL = canvas.toDataURL('image/png');
    downloadFile(dataURL, `Dashboard_${timeSpanDate.startDate}_image.png`, 'image/png');
    toast.success('Image downloaded successfully');
  };

  /**
   * Function to handle button clicks for download, upload and capture.
   * @param id - ID of the function.
   */
  const handleClickButton = async (id: string): Promise<void> => {
    if (id === 'download') {
      sendCustomEventToGa(GA_CATEGORIES.download, 'download-clicked');
      handleDownload();
    } else if (id === 'upload') {
      handleIngest();
    } else {
      await handleScreenCapture();
    }
  };

  /**
   * Calls onChangeTimeFilter conditionally.
   * @param type - Time span selected.
   */
  const handleOnTimeFilterChange = (type: TimeSpan): void => {
    if (type === TimeSpan.Custom) {
      setCustomDatePicker(true);
    } else {
      onChangeTimeFilter(type);
    }
  };

  /**
   * A function to select date range.
   * @param value - Selected start and end time.
   */
  function handleApply(value: TimeSpanDateAttributes): void {
    if (value.startDate) {
      setTimeSpanDate(value);
      setAppliedFilter({
        key: Filters.timeSpanCustom,
        data: value
      });
    }
    onChangeTimeFilter(TimeSpan.Custom);
    setCustomDatePicker(false);
  }

  /**
   * A function to remove date range, updates the state setStartDate and setEndDate to null.
   */
  function handleCancel(): void {
    setCustomDatePicker(false);
    onChangeTimeFilter(timeSpan);
  }

  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const TIME_SPAN_PLACEHOLDER =
    timeSpan === TimeSpan.Custom
      ? `${moment(timeSpanDate.startDate).tz(timeZone).format('Do MMM')} - ${moment(
          timeSpanDate.endDate
        )
          .tz(timeZone)
          .format('Do MMM')}`
      : timeSpan;

  const RESOLUTIONS: Option<string>[] = getResolutionOptions(
    timeSpanDate.startDate,
    timeSpanDate.endDate
  ).map((option) => ({ name: option.label, id: option.value }));

  const isPublicView = sessionStorage.getItem('isPublicView');

  return (
    <div className="flex flex-col md:flex-row justify-between gap-3">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div className="flex gap-2 justify-between items-end relative">
          {currentUser?.userName === 'suresh@potsdamsensors.com' && <ClientChange />}
          <DropdownControl
            label="Group / Device"
            controlPlaceholder={dataFilter?.name || DAHBOARD_DEFAULT_FILTER}
            options={[OVERVIEW_GROUP, ...GROUPS]}
            onSelect={(option) => setDataFilter({ ...option, type: 'group' })}
            onChildSelect={(option) => setDataFilter({ ...option, type: 'device' })}
          />
          <DropdownControl
            label="Time range"
            controlPlaceholder={TIME_SPAN_PLACEHOLDER}
            options={TIME_SPANS}
            onSelect={(option) => handleOnTimeFilterChange(option.name as TimeSpan)}
          />
          {timeSpan !== TimeSpan.RealTime && (
            <DropdownControl
              label="Resolution"
              controlPlaceholder={resolutionLabel(
                resolution,
                timeSpanDate.startDate,
                timeSpanDate.endDate
              )}
              options={RESOLUTIONS}
              onSelect={(option) => setResolution(option.id)}
            />
          )}
          {customDatePicker && (
            <div className="absolute z-20 top-full mt-1">
              <DateRangePicker
                timeSpanDate={timeSpanDate}
                handleCancel={() => handleCancel()}
                onApply={(value) => handleApply(value)}
                minDate={customMinDate}
              />
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-between items-end">
          <div className="flex gap-3 border-gray-300 border items-center shadow-xs rounded px-4 py-1.5 bg-white dark:bg-background-800 dark:border-background-700">
            <RefreshButton refreshing={refreshing} onRefresh={onRefresh} />
            <p className="text-pink text-sm font-medium border-l border-l-gray-400 dark:border-l-background-400 pl-2.5 border-opacity-75">
              AUTO
            </p>
            <Procard />
          </div>
          <DashboardSyncCountDown lastSynced={lastSynced} />
        </div>
      </div>
      <div className="flex gap-3 items-end justify-between">
        {/* <span className="material-icons-outlined text-gray-400">calendar_month</span> */}
        {availableViews.length > 1 && (
          <div className="mr-3">
            <ViewModeToggle view={view} onChange={setView} availableViews={availableViews} />
          </div>
        )}
        {!isPublicView &&
          IconSourceDescription.map((curr) => (
            <div key={curr.id} className="group flex relative">
              <button
                onClick={() => handleClickButton(curr.id)}
                type="button"
                className="material-icons-outlined cursor-pointer text-gray-400"
              >
                {curr.icon}
              </button>
              <div
                role="button"
                tabIndex={0}
                onKeyDown={() => null}
                onClick={() => handleClickButton(curr.id)}
                className={`${curr.class} group-hover:block absolute w-[280px] top-5 z-50`}
              >
                <InfoCard tipPosition="right" title={curr.title} description={curr.description} />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
